import React, { useState } from 'react';

import Button from '../../../components/ui/Button';

const ServiceAvailabilityMap = () => {
  const [selectedService, setSelectedService] = useState('all');

  const services = [
    { id: 'all', label: 'All Services', color: '#3B82F6' },
    { id: 'food', label: 'Food Delivery', color: '#10B981' },
    { id: 'services', label: 'Home Services', color: '#F59E0B' },
    { id: 'property', label: 'Property', color: '#8B5CF6' },
    { id: 'tours', label: 'Tours & Travel', color: '#EF4444' }
  ];

  const regions = [
    {
      id: 1,
      name: 'North America',
      cities: [
        { name: 'New York', status: 'operational', uptime: 99.9, lat: 40.7128, lng: -74.0060 },
        { name: 'Los Angeles', status: 'operational', uptime: 99.8, lat: 34.0522, lng: -118.2437 },
        { name: 'Chicago', status: 'degraded', uptime: 97.5, lat: 41.8781, lng: -87.6298 },
        { name: 'Toronto', status: 'operational', uptime: 99.7, lat: 43.6532, lng: -79.3832 }
      ]
    },
    {
      id: 2,
      name: 'Europe',
      cities: [
        { name: 'London', status: 'operational', uptime: 99.9, lat: 51.5074, lng: -0.1278 },
        { name: 'Paris', status: 'operational', uptime: 99.6, lat: 48.8566, lng: 2.3522 },
        { name: 'Berlin', status: 'maintenance', uptime: 95.0, lat: 52.5200, lng: 13.4050 },
        { name: 'Madrid', status: 'operational', uptime: 99.4, lat: 40.4168, lng: -3.7038 }
      ]
    },
    {
      id: 3,
      name: 'Asia Pacific',
      cities: [
        { name: 'Tokyo', status: 'operational', uptime: 99.9, lat: 35.6762, lng: 139.6503 },
        { name: 'Singapore', status: 'operational', uptime: 99.8, lat: 1.3521, lng: 103.8198 },
        { name: 'Sydney', status: 'degraded', uptime: 98.2, lat: -33.8688, lng: 151.2093 },
        { name: 'Mumbai', status: 'operational', uptime: 99.5, lat: 19.0760, lng: 72.8777 }
      ]
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational': return 'bg-success';
      case 'degraded': return 'bg-warning';
      case 'maintenance': return 'bg-primary';
      case 'outage': return 'bg-error';
      default: return 'bg-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'operational': return 'Operational';
      case 'degraded': return 'Degraded';
      case 'maintenance': return 'Maintenance';
      case 'outage': return 'Outage';
      default: return 'Unknown';
    }
  };

  const allCities = regions?.flatMap(region => region?.cities);
  const operationalCount = allCities?.filter(city => city?.status === 'operational')?.length;
  const totalCount = allCities?.length;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Service Availability</h3>
          <p className="text-sm text-muted-foreground">
            Geographic service status • {operationalCount}/{totalCount} cities operational
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <div className="flex bg-muted rounded-md p-1">
            {services?.map((service) => (
              <button
                key={service?.id}
                onClick={() => setSelectedService(service?.id)}
                className={`px-3 py-1 text-xs font-medium rounded transition-hover ${
                  selectedService === service?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {service?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Map Placeholder */}
      <div className="relative bg-muted/30 rounded-lg h-64 mb-6 overflow-hidden">
        <iframe
          width="100%"
          height="100%"
          loading="lazy"
          title="Global Service Availability Map"
          referrerPolicy="no-referrer-when-downgrade"
          src="https://www.google.com/maps?q=40.7128,-74.0060&z=2&output=embed"
          className="rounded-lg"
        />
        
        {/* Overlay with status indicators */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-4 left-4 bg-card/90 backdrop-blur-sm border border-border rounded-lg p-3">
            <div className="flex items-center space-x-4 text-xs">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-success rounded-full"></div>
                <span>Operational</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-warning rounded-full"></div>
                <span>Degraded</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span>Maintenance</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-error rounded-full"></div>
                <span>Outage</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Regional Status */}
      <div className="space-y-4">
        {regions?.map((region) => (
          <div key={region?.id} className="border border-border rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-3">{region?.name}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {region?.cities?.map((city, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-md"
                >
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${getStatusColor(city?.status)}`}></div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{city?.name}</p>
                      <p className="text-xs text-muted-foreground">{city?.uptime}% uptime</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" iconName="ExternalLink">
                    Details
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Quick Actions */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" iconName="RefreshCw">
              Refresh Status
            </Button>
            <Button variant="ghost" size="sm" iconName="Settings">
              Configure Regions
            </Button>
          </div>
          <Button variant="ghost" size="sm" iconName="Download">
            Export Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServiceAvailabilityMap;